import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
// import MessageModal from "../../components/MessageModal"
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import moment from "moment";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import format from "date-fns/format";
import getDay from "date-fns/getDay";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import ptBR from "date-fns/locale/pt-BR";
import "moment/locale/pt-br";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import {
  formatCalendarTime,
  formatScheduleTime,
  scheduleDateToCalendarDate
} from "../../utils/scheduleDate";

import "./Schedules.css"; // Importe o arquivo CSS

// Defina a função getUrlParam antes de usá-la
function getUrlParam(paramName) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(paramName);
}

const locales = {
  "pt-BR": ptBR,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const formatPtBr = (date, pattern) => format(date, pattern, { locale: ptBR });

const calendarFormats = {
  dateFormat: (date) => formatPtBr(date, "dd"),
  dayFormat: (date) => formatPtBr(date, "dd EEE"),
  weekdayFormat: (date) => formatPtBr(date, "EEEE"),
  monthHeaderFormat: (date) => formatPtBr(date, "MMMM yyyy"),
  dayHeaderFormat: (date) => formatPtBr(date, "EEEE, dd 'de' MMMM"),
  dayRangeHeaderFormat: ({ start, end }) =>
    `${formatPtBr(start, "dd 'de' MMM")} - ${formatPtBr(end, "dd 'de' MMM 'de' yyyy")}`,
  agendaHeaderFormat: ({ start, end }) =>
    `${formatPtBr(start, "dd 'de' MMMM")} - ${formatPtBr(end, "dd 'de' MMMM 'de' yyyy")}`,
  agendaDateFormat: (date) => formatPtBr(date, "dd/MM EEE"),
  timeGutterFormat: (date) => formatCalendarTime(date),
  agendaTimeFormat: (date) => formatCalendarTime(date),
  eventTimeRangeFormat: ({ start, end }) =>
    `${formatCalendarTime(start)} - ${formatCalendarTime(end)}`,
};

var defaultMessages = {
  date: i18n.t("schedules.date"),
  time: i18n.t("schedules.time"),
  event: i18n.t("schedules.event"),
  allDay: i18n.t("schedules.allDay"),
  week: i18n.t("schedules.week"),
  work_week: i18n.t("schedules.work_week"),
  day: i18n.t("schedules.day"),
  month: i18n.t("schedules.month"),
  previous: i18n.t("schedules.previous"),
  next: i18n.t("schedules.next"),
  yesterday: i18n.t("schedules.yesterday"),
  tomorrow: i18n.t("schedules.tomorrow"),
  today: i18n.t("schedules.today"),
  agenda: i18n.t("schedules.agenda"),
  noEventsInRange: i18n.t("schedules.noEventsInRange"),
  showMore: function showMore(total) {
    return "+" + total + " mais";
  },
};

const reducer = (state, action) => {
  if (action.type === "LOAD_SCHEDULES") {
    const schedules = action.payload;
    const newSchedules = [];

    schedules.forEach((schedule) => {
      const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
      if (scheduleIndex !== -1) {
        state[scheduleIndex] = schedule;
      } else {
        newSchedules.push(schedule);
      }
    });

    return [...state, ...newSchedules];
  }

  if (action.type === "UPDATE_SCHEDULES") {
    const schedule = action.payload;
    const scheduleIndex = state.findIndex((s) => s.id === schedule.id);

    if (scheduleIndex !== -1) {
      state[scheduleIndex] = schedule;
      return [...state];
    } else {
      return [schedule, ...state];
    }
  }

  if (action.type === "DELETE_SCHEDULE") {
    const scheduleId = action.payload;

    const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
    if (scheduleIndex !== -1) {
      state.splice(scheduleIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  page: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: theme.mode === "light" ? "#f3f6fb" : theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 20,
    gap: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  pageTitle: {
    margin: 0,
    color: theme.palette.text.primary,
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: 0,
  },
  counter: {
    borderRadius: 999,
    background: "#e8f1ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: 13,
    padding: "5px 12px",
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  searchField: {
    width: 340,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    "& .MuiOutlinedInput-root": {
      height: 44,
      background: "#fff",
      borderRadius: 12,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      "& fieldset": {
        borderColor: "#dbe3ef",
      },
      "&:hover fieldset": {
        borderColor: "#9db7dc",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2563eb",
        borderWidth: 1,
      },
    },
  },
  addButton: {
    height: 44,
    borderRadius: 12,
    padding: "0 18px",
    textTransform: "none",
    fontWeight: 700,
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    border: "1px solid #e3e9f3",
    borderRadius: 18,
    background: theme.mode === "light" ? "#fff" : theme.palette.background.paper,
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.07)",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
      borderRadius: 14,
    },
  },
  calendarToolbar: {
    minHeight: 620,
  },
}));

const ScheduleEvent = ({ event, handleDeleteSchedule, handleEditSchedule }) => {
  const schedule = event.schedule;
  const contactName = schedule?.contact?.name || "Agendamento";
  const sendAtLabel = formatCalendarTime(event.start) || formatScheduleTime(schedule?.sendAt);

  return (
    <div className="schedule-event" title={`${sendAtLabel} ${contactName}`.trim()}>
      {sendAtLabel && <span className="schedule-event-time">{sendAtLabel}</span>}
      <span className="schedule-event-title">{contactName}</span>
      <span className="schedule-event-actions">
        <DeleteOutlineIcon
          onClick={(event) => {
            event.stopPropagation();
            handleDeleteSchedule(schedule.id);
          }}
          className="delete-icon"
        />
        <EditIcon
          onClick={(event) => {
            event.stopPropagation();
            handleEditSchedule(schedule);
          }}
          className="edit-icon"
        />
      </span>
    </div>
  );
};

const Schedules = () => {
  const classes = useStyles();
  const history = useHistory();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deletingSchedule, setDeletingSchedule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [schedules, dispatch] = useReducer(reducer, []);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [contactId, setContactId] = useState(+getUrlParam("contactId"));

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useSchedules) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
  }, [user, history, getPlanCompany]);

  const fetchSchedules = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_SCHEDULES", payload: data.schedules });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  const handleOpenScheduleModalFromContactId = useCallback(() => {
    if (contactId) {
      handleOpenScheduleModal();
    }
  }, [contactId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchSchedules();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [
    searchParam,
    pageNumber,
    contactId,
    fetchSchedules,
    handleOpenScheduleModalFromContactId,
  ]);

  useEffect(() => {
    // handleOpenScheduleModalFromContactId();
    // const socket = socketManager.GetSocket(user.companyId, user.id);

    const onCompanySchedule = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
      }
    };

    socket.on(`company${user.companyId}-schedule`, onCompanySchedule);

    return () => {
      socket.off(`company${user.companyId}-schedule`, onCompanySchedule);
    };
  }, [socket, user.companyId]);

  const cleanContact = () => {
    setContactId("");
  };

  const handleOpenScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setSelectedSchedule(null);
    setScheduleModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleModalOpen(true);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success(i18n.t("schedules.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSchedule(null);
    setSearchParam("");
    setPageNumber(1);

    dispatch({ type: "RESET" });
    setPageNumber(1);
    await fetchSchedules();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const calendarEvents = schedules.map((schedule) => {
    const start = scheduleDateToCalendarDate(schedule.sendAt);

    return {
      title: schedule?.contact?.name || "Agendamento",
      start,
      end: moment(start).isValid()
        ? moment(start).add(30, "minutes").toDate()
        : start,
      schedule,
    };
  });

  return (
    <MainContainer>
      <div className={classes.page}>
      <ConfirmationModal
        title={
          deletingSchedule &&
          `${i18n.t("schedules.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
      >
        {i18n.t("schedules.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {scheduleModalOpen && (
        <ScheduleModal
          open={scheduleModalOpen}
          onClose={handleCloseScheduleModal}
          reload={fetchSchedules}
          // aria-labelledby="form-dialog-title"
          scheduleId={selectedSchedule ? selectedSchedule.id : null}
          contactId={contactId}
          cleanContact={cleanContact}
          user={user}
        />
      )}
      <div className={classes.header}>
        <div className={classes.headerText}>
          <div className={classes.titleRow}>
            <h1 className={classes.pageTitle}>
              {i18n.t("schedules.title")}
            </h1>
            <span className={classes.counter}>
              {schedules.length} {schedules.length === 1 ? "agendamento" : "agendamentos"}
            </span>
          </div>
          <span className={classes.subtitle}>
            Gerencie mensagens programadas e recorrentes
          </span>
        </div>
        <div className={classes.headerActions}>
          <TextField
            placeholder="Buscar por contato ou mensagem"
            type="search"
            value={searchParam}
            onChange={handleSearch}
            variant="outlined"
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#64748b" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenScheduleModal}
            className={classes.addButton}
            startIcon={<AddIcon />}
          >
            Novo Agendamento
          </Button>
        </div>
      </div>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Calendar
          messages={defaultMessages}
          formats={calendarFormats}
          localizer={localizer}
          culture="pt-BR"
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          dayLayoutAlgorithm="no-overlap"
          style={{ minHeight: 620 }}
          className={classes.calendarToolbar}
          components={{
            event: (props) => (
              <ScheduleEvent
                {...props}
                handleDeleteSchedule={handleDeleteSchedule}
                handleEditSchedule={handleEditSchedule}
              />
            ),
          }}
          tooltipAccessor={(event) => event.title}
          onSelectEvent={(event) => handleEditSchedule(event.schedule)}
        />
      </Paper>
      </div>
    </MainContainer>
  );
};

export default Schedules;
